<script setup lang="ts">
/**
 * Step 2 du checkout adhésion (multi-member) :
 * cartes participants avec identité + options + custom fields par plan.
 */
import { useErreursEffaceesALaSaisie } from "~/composables/useErreursEffaceesALaSaisie";
import { exempleTelephone, messageTelephoneInvalide, telephoneValide } from "@donoor/utils";
import type {
  MembershipPlanOption,
  MembershipCustomField,
} from "~/types/membership";
import { isValidEmailStrict } from "@donoor/validators";
import { allerAuPremierChampEnErreur, validerChamps } from "~/utils/validationChamps";
import { usePaysAssociation } from "~/composables/usePaysAssociation";
import { BORDURE_CHAMP_PUBLIC, BORDURE_CHAMP_PUBLIC_ERREUR } from "~/utils/stylesChamps";

const { t } = useI18n();
const { formatCurrency } = useFormatLangue();

// Pays de l'association : un téléphone tapé sans indicatif s'y lit.
const paysAsso = usePaysAssociation();

interface ParticipantLocal {
  planId: string;
  planTitle: string;
  planPrice: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  selectedOptions: Record<string, boolean>;
  customFieldValues: Record<string, unknown>;
}

const props = defineProps<{
  participants: ParticipantLocal[];
  payerIndex: number;
  getPlanOptions: (planId: string) => MembershipPlanOption[];
  getPlanCustomFields: (planId: string) => MembershipCustomField[];
  allParticipantsCustomFieldsValid: boolean;
  isFileUploading: (fieldId: string, idx: number) => boolean;
  isFileUploaded: (fieldId: string, idx: number) => boolean;
  getFileUploadError: (fieldId: string, idx: number) => string | undefined;
}>();

const emit = defineEmits<{
  (e: 'set-payer', idx: number): void;
  (e: 'back' | 'continue'): void;
  (e: 'upload-file', file: File, fieldId: string, idx: number): void;
  (e: 'remove-file', fieldId: string, idx: number): void;
}>();

function toggleOption(participant: ParticipantLocal, opt: MembershipPlanOption) {
  if (opt.is_required) return;
  participant.selectedOptions[opt.id] = !participant.selectedOptions[opt.id];
}

// ── Validation à la demande ────────────────────────────────────────────────
//
// 🔴 Le bouton « Continuer » était DÉSACTIVÉ tant que le formulaire était
// invalide : cliquer ne produisait rien et rien ne disait POURQUOI (Karim
// 2026-09-21). Il reste donc actif, et c'est le clic qui valide : chaque champ
// fautif se cerne de rouge, affiche sa raison, et la page défile jusqu'au
// premier d'entre eux.
//
// Les erreurs n'apparaissent qu'APRÈS une tentative (`aTenteDeContinuer`) :
// rougir un formulaire vierge est agressif et n'apprend rien.

/** Erreurs d'identité, par index de participant. */
const erreursIdentite = ref<Record<number, Record<string, string>>>({});
/** Erreurs de champs personnalisés, par index de participant. */
const erreursChamps = ref<Record<number, Record<string, string>>>({});
const aTenteDeContinuer = ref(false);


/** Préfixe d'id UNIQUE par participant : deux participants partagent les mêmes ids de champ. */
function prefixeParticipant(idx: number): string {
  return `p${idx}`;
}

function validerIdentite(p: ParticipantLocal): Record<string, string> {
  const e: Record<string, string> = {};
  if (!p.firstName?.trim()) e.firstName = t("adhesion.aslugStepParticipants.prenomObligatoire");
  if (!p.lastName?.trim()) e.lastName = t("adhesion.aslugStepParticipants.nomObligatoire");
  if (!p.email?.trim()) {
    e.email = t("adhesion.aslugStepParticipants.emailObligatoire");
  } else if (!isValidEmailStrict(p.email)) {
    e.email = t("adhesion.aslugStepParticipants.emailInvalideExemple");
  }
  // Téléphone FACULTATIF, mais vérifié s'il est rempli — lu dans le pays
  // de l'association (règle partagée @donoor/utils).
  if (p.phone?.trim() && !telephoneValide(p.phone, paysAsso.value)) {
    e.phone = messageTelephoneInvalide(paysAsso.value);
  }
  return e;
}

/**
 * Une place = une personne : la même personne (même prénom et nom, même
 * email — à la casse, aux accents et à l'ordre prénom/nom près) deux fois au
 * même tarif est refusée. L'association voyait 4 adhérents pour une seule
 * personne inscrite 4 fois. Même règle côté API (`participantsEnDouble.ts`).
 */
function clePersonne(p: ParticipantLocal): string | null {
  const email = p.email?.trim().toLowerCase();
  const nom = `${p.firstName ?? ""} ${p.lastName ?? ""}`
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .split(/[\s\-']+/)
    .filter(Boolean)
    .sort()
    .join(" ");
  return email && nom ? `${p.planId}|${email}|${nom}` : null;
}

/**
 * Place en double → numéro (0, 1…) de la place qu'elle répète. Recalculé à
 * chaque frappe : le rouge disparaît dès que le prénom, le nom ou l'email
 * change.
 */
const doublons = computed(() => {
  const vus = new Map<string, number>();
  const res: Record<number, number> = {};
  props.participants.forEach((p, idx) => {
    const cle = clePersonne(p);
    if (!cle) return;
    const premier = vus.get(cle);
    if (premier === undefined) vus.set(cle, idx);
    else res[idx] = premier;
  });
  return res;
});

/**
 * Seul le PRÉNOM est signalé : le nom de famille et l'email peuvent être les
 * mêmes (une maman qui inscrit ses deux enfants avec son email). L'encart en
 * haut de la place explique la règle.
 */
function messageDoublon(idx: number, champ: string): string | undefined {
  if (champ !== "firstName" || doublons.value[idx] === undefined) return undefined;
  return t("adhesion.aslugStepParticipants.doublonPrenom");
}

/** Place en double, après une tentative de continuer : n° de la place répétée (1, 2…). */
function doublonAffiche(idx: number): number | undefined {
  const premier = doublons.value[idx];
  return aTenteDeContinuer.value && premier !== undefined ? premier + 1 : undefined;
}

function auClicContinuer() {
  aTenteDeContinuer.value = true;
  const parIdentite: Record<number, Record<string, string>> = {};
  const parChamps: Record<number, Record<string, string>> = {};

  props.participants.forEach((p, idx) => {
    const eIdentite = validerIdentite(p);
    if (Object.keys(eIdentite).length > 0) parIdentite[idx] = eIdentite;

    const champs = props.getPlanCustomFields(p.planId).map((f) => ({
      id: String(f.id),
      label: f.label,
      type: f.field_type as TypeChampPublic,
      requis: !!f.is_required,
    }));
    const eChamps = validerChamps(champs, p.customFieldValues, paysAsso.value);
    if (Object.keys(eChamps).length > 0) parChamps[idx] = eChamps;
  });

  erreursIdentite.value = parIdentite;
  erreursChamps.value = parChamps;

  // Premier participant fautif, identité d'abord (elle est plus haut à l'écran).
  for (let idx = 0; idx < props.participants.length; idx++) {
    const eId = parIdentite[idx];
    if (eId && Object.keys(eId).length > 0) {
      allerAuPremierChampEnErreur(eId, prefixeParticipant(idx));
      return;
    }
    const eCh = parChamps[idx];
    if (eCh && Object.keys(eCh).length > 0) {
      allerAuPremierChampEnErreur(eCh, prefixeParticipant(idx));
      return;
    }
    if (doublons.value[idx] !== undefined) {
      allerAuPremierChampEnErreur({ firstName: "doublon" }, prefixeParticipant(idx));
      return;
    }
  }

  emit("continue");
}

// Identité à plat (« 0.firstName ») : une erreur s'efface dès qu'on
// modifie le champ, participant par participant (cf. composable).
const identiteEffacable = useErreursEffaceesALaSaisie(
  () => {
    const plat: Record<string, string> = {};
    for (const [idx, e] of Object.entries(erreursIdentite.value)) {
      for (const [champ, msg] of Object.entries(e)) plat[`${idx}.${champ}`] = msg;
    }
    return plat;
  },
  () => {
    const plat: Record<string, unknown> = {};
    props.participants.forEach((p, idx) => {
      for (const champ of ["firstName", "lastName", "email", "phone"]) {
        plat[`${idx}.${champ}`] = (p as unknown as Record<string, unknown>)[champ];
      }
    });
    return plat;
  },
);

/** Erreur d'un champ d'identité — masquée tant qu'on n'a pas tenté de continuer. */
function erreurIdentite(idx: number, champ: string): string | undefined {
  if (!aTenteDeContinuer.value) return undefined;
  return identiteEffacable.erreurDe(`${idx}.${champ}`) ?? messageDoublon(idx, champ);
}

/** Classes d'un champ d'identité selon son état d'erreur. */
function classesIdentite(idx: number, champ: string): string {
  const base =
    "w-full px-4 py-3 rounded-xl bg-white dark:bg-[#0f0f0f] text-sm text-[#2d2e5c] dark:text-white placeholder-gray-400 transition-colors";
  return `${base} ${erreurIdentite(idx, champ) ? BORDURE_CHAMP_PUBLIC_ERREUR : BORDURE_CHAMP_PUBLIC}`;
}

/** Téléphone : chiffres et séparateurs seulement (cf. AslugCustomFieldsBlock). */
function filtrerTelephone(participant: ParticipantLocal, e: Event) {
  const cible = e.target as HTMLInputElement;
  const propre = cible.value.replace(/[^\d\s+().-]/g, "");
  if (cible.value !== propre) cible.value = propre;
  participant.phone = propre;
}
</script>

<template>
  <div class="p-3 md:p-5">
    <div class="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-800">
      <UIcon name="i-lucide-info" class="size-4 text-[#717198]" />
      <p class="text-xs text-[#717198] dark:text-gray-500">{{ $t('adhesion.aslugStepParticipants.champsObligatoiresIntro') }}<span class="text-red-500">*</span>{{ $t('adhesion.aslugStepParticipants.champsObligatoiresFin') }}</p>
    </div>

    <div class="space-y-4">
      <div
        v-for="(participant, idx) in participants"
        :key="idx"
        class="rounded-2xl border border-gray-200 dark:border-[#262626] overflow-hidden"
      >
        <!-- Card header -->
        <div class="flex items-center justify-between px-5 py-3 bg-gray-50 dark:bg-[#0f0f0f] border-b border-gray-100 dark:border-[#1e1e1e]">
          <div class="flex items-center gap-3">
            <div class="w-7 h-7 rounded-full flex items-center justify-center shrink-0" :class="payerIndex === idx ? 'bg-emerald-500' : 'bg-[#212121] dark:bg-white'">
              <UIcon v-if="payerIndex === idx" name="i-lucide-credit-card" class="size-3.5 text-white" />
              <span v-else class="text-xs font-bold text-white dark:text-[#212121]">{{ idx + 1 }}</span>
            </div>
            <div>
              <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white">
                {{ $t('adhesion.aslugStepParticipants.participantTitre', { n: idx + 1, planTitre: participant.planTitle }) }}
                <span class="text-xs font-normal text-[#717198] dark:text-gray-400 ml-1">({{ participant.planPrice === 0 ? $t('adhesion.aslugStepParticipants.prixGratuit') : formatCurrency(participant.planPrice) }})</span>
              </p>
            </div>
          </div>
          <button
            v-if="participants.length > 1"
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors"
            :class="payerIndex === idx
              ? 'bg-emerald-500 text-white'
              : 'accent-btn hover:opacity-90'"
            @click="$emit('set-payer', idx)"
          >
            <UIcon name="i-lucide-credit-card" class="size-3" />
            {{ payerIndex === idx ? $t('adhesion.aslugStepParticipants.effectuePaiement') : $t('adhesion.aslugStepParticipants.designerPayeur') }}
          </button>
          <span v-else class="text-[11px] text-[#717198] dark:text-gray-500">{{ $t('adhesion.aslugStepParticipants.effectueraPaiement') }}</span>
        </div>

        <!-- Card body -->
        <div class="p-5 space-y-4">
          <!-- Même personne qu'une place au-dessus : surface neutre, pastille
               ambre pleine (jamais une couleur sur un fond de sa teinte). -->
          <div
            v-if="doublonAffiche(idx) !== undefined"
            role="alert"
            class="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#161616] p-4"
          >
            <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-500">
              <UIcon name="i-lucide-users" class="size-3.5 text-white" />
            </span>
            <div class="space-y-1">
              <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ $t('adhesion.aslugStepParticipants.doublonTitre', { n: doublonAffiche(idx) }) }}</p>
              <p class="text-sm text-[#505870] dark:text-gray-400">{{ $t('adhesion.aslugStepParticipants.doublonTexte') }}</p>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label :for="`${prefixeParticipant(idx)}-firstName`" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ $t('adhesion.aslugStepParticipants.prenomLabel') }} <span class="text-red-500">*</span></label>
              <input :id="`${prefixeParticipant(idx)}-firstName`" v-model="participant.firstName" type="text" autocomplete="given-name" :placeholder="$t('adhesion.aslugStepParticipants.placeholderPrenom')" :aria-invalid="!!erreurIdentite(idx, 'firstName') || undefined" :class="classesIdentite(idx, 'firstName')" />
              <p v-if="erreurIdentite(idx, 'firstName')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
                <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurIdentite(idx, 'firstName') }}</span>
              </p>
            </div>
            <div>
              <label :for="`${prefixeParticipant(idx)}-lastName`" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ $t('adhesion.aslugStepParticipants.nomLabel') }} <span class="text-red-500">*</span></label>
              <input :id="`${prefixeParticipant(idx)}-lastName`" v-model="participant.lastName" type="text" autocomplete="family-name" :placeholder="$t('adhesion.aslugStepParticipants.placeholderNom')" :aria-invalid="!!erreurIdentite(idx, 'lastName') || undefined" :class="classesIdentite(idx, 'lastName')" />
              <p v-if="erreurIdentite(idx, 'lastName')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
                <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurIdentite(idx, 'lastName') }}</span>
              </p>
            </div>
          </div>
          <div>
            <label :for="`${prefixeParticipant(idx)}-email`" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ $t('adhesion.aslugStepParticipants.emailLabel') }} <span class="text-red-500">*</span></label>
            <input :id="`${prefixeParticipant(idx)}-email`" v-model="participant.email" type="email" inputmode="email" autocomplete="email" :placeholder="$t('adhesion.aslugStepParticipants.placeholderEmail')" :aria-invalid="!!erreurIdentite(idx, 'email') || undefined" :class="classesIdentite(idx, 'email')" />
            <p v-if="erreurIdentite(idx, 'email')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
              <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurIdentite(idx, 'email') }}</span>
            </p>
          </div>
          <div>
            <label :for="`${prefixeParticipant(idx)}-phone`" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ $t('adhesion.aslugStepParticipants.telephoneLabel') }}</label>
            <input :id="`${prefixeParticipant(idx)}-phone`" :value="participant.phone" type="tel" inputmode="tel" autocomplete="tel" :placeholder="exempleTelephone(paysAsso)" :aria-invalid="!!erreurIdentite(idx, 'phone') || undefined" :class="classesIdentite(idx, 'phone')" @input="filtrerTelephone(participant, $event)" />
            <p v-if="erreurIdentite(idx, 'phone')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
              <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurIdentite(idx, 'phone') }}</span>
            </p>
          </div>

          <!-- Options per participant -->
          <div v-if="getPlanOptions(participant.planId).length > 0">
            <p class="text-xs font-semibold text-[#505870] dark:text-gray-400 uppercase tracking-wide mb-2">{{ $t('adhesion.aslugStepParticipants.optionsTitre') }}</p>
            <div class="space-y-2">
              <button
                v-for="opt in getPlanOptions(participant.planId)"
                :key="opt.id"
                type="button"
                :data-testid="`membership-option-${opt.id}`"
                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left"
                :class="participant.selectedOptions[opt.id]
                  ? 'border-[#212121] dark:border-white bg-[#f5f5f5] dark:bg-[#1a1a1a]'
                  : 'border-gray-200 dark:border-[#262626] hover:border-gray-300 dark:hover:border-gray-600'"
                :disabled="opt.is_required"
                @click="toggleOption(participant, opt)"
              >
                <div
                  class="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors"
                  :class="participant.selectedOptions[opt.id]
                    ? 'border-[#212121] dark:border-white bg-[#212121] dark:bg-white'
                    : 'border-gray-300 dark:border-gray-600'"
                >
                  <UIcon v-if="participant.selectedOptions[opt.id]" name="i-lucide-check" class="size-3 text-white dark:text-[#212121]" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-[#2d2e5c] dark:text-white">
                    {{ opt.name }}
                    <span v-if="opt.is_required" class="text-xs text-[#717198] dark:text-gray-500 ml-1">{{ $t('adhesion.aslugStepParticipants.inclus') }}</span>
                  </p>
                  <p v-if="opt.description" class="text-[11px] text-[#717198] dark:text-gray-400 mt-0.5">{{ opt.description }}</p>
                  <p v-if="opt.max_quantity" class="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 font-medium">{{ $t('adhesion.aslugStepParticipants.placesTotal', { n: opt.max_quantity }) }}</p>
                </div>
                <span class="text-xs font-semibold shrink-0" :class="Number(opt.price) > 0 ? 'text-[#2d2e5c] dark:text-white' : 'text-emerald-600 dark:text-emerald-400'">
                  {{ Number(opt.price) > 0 ? `+${formatCurrency(Number(opt.price))}` : $t('adhesion.aslugStepParticipants.gratuit') }}
                </span>
              </button>
            </div>
          </div>

          <!-- Custom fields per participant -->
          <div v-if="getPlanCustomFields(participant.planId).length > 0" class="space-y-4 pt-2">
            <AslugCustomFieldsBlock
              :fields="getPlanCustomFields(participant.planId)"
              :values="participant.customFieldValues"
              :participant-index="idx"
              :is-file-uploading="isFileUploading"
              :is-file-uploaded="isFileUploaded"
              :get-file-upload-error="getFileUploadError"
              :erreurs="aTenteDeContinuer ? erreursChamps[idx] : undefined"
              :prefixe-id="prefixeParticipant(idx)"
              @upload-file="(file, fieldId, i) => $emit('upload-file', file, fieldId, i)"
              @remove-file="(fieldId, i) => $emit('remove-file', fieldId, i)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="flex items-center justify-between mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
      <button class="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium text-[#505870] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors" @click="$emit('back')">
        <UIcon name="i-lucide-arrow-left" class="size-4" /> {{ $t('commun.actions.retour') }}
      </button>
      <!-- JAMAIS désactivé : c'est le clic qui valide et explique ce qui bloque.
           Un bouton grisé laisse l'adhérent sans aucune piste. -->
      <button data-testid="btn-step2-continue" class="px-8 py-3 accent-btn rounded-xl text-sm font-semibold hover:bg-[#333] dark:hover:bg-gray-100 transition-colors" @click="auClicContinuer">
        {{ $t('commun.actions.continuer') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Boutons d'action — couleur choisie dans la configuration de l'adhésion.
   Repli #212121 quand aucune couleur n'est posée. Même classe et mêmes
   variables que la billetterie (BslugStepRecap) : une seule règle pour les
   trois domaines. Karim 2026-09-12. */
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
